import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { ParseErrorTypes } from '@nimbus-library/features/graph/enums/parser-error-types.enum';
import { SearchOptions } from '@nimbus-library/features/graph/enums/search-options.enum';
import { defaultParseErrorMessages } from '@nimbus-library/features/graph/models/search-expression-query-factories.model';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { LocalizableError } from '@nimbus-library/features/shared/models/localizable-error.model';

export type SearchExpression = { [key in SearchOptions]?: string };
export type ParseErrorCollection = { [key in ParseErrorTypes]?: ParseError };

export interface ParseError {
  index: number;
  message: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class SearchParserResult<TLock = any> {
  public searchExpression: SearchExpression = {};
  public plainSearchText: string;
  public originalInput: string;
  public errors: IMap<ParseErrorCollection> = {};
  public inputError?: ParseError;
  public incompatibleFiltersError?: ParseError;
  public searchOptions: SearchOptions[];
  public compatibilityLock?: TLock;
  public defaultSearchFilter?: string;

  public constructor(searchOptions: SearchOptions[], defaultSearchFilter?: string) {
    this.searchOptions = searchOptions;
    this.defaultSearchFilter = defaultSearchFilter;
  }

  public hasError(expression?: keyof SearchExpression): boolean {
    if (!expression) {
      return !!this.inputError;
    }
    return (
      !!this.inputError || (this.errors[expression] && Object.keys(this.errors[expression]).some((key) => !!this.errors[expression][key]))
    );
  }

  public getSearchExpression(key: keyof SearchExpression): string {
    return this.searchExpression[key];
  }

  public hasSearchExpressionsFilled(): boolean {
    return Object.keys(this.searchExpression).some((key) => !!this.searchExpression[key]);
  }

  public getSearchExpressionsUsed(): SearchOptions[] {
    return Object.keys(this.searchExpression).filter((key) => this.searchOptions.includes(key as SearchOptions)) as SearchOptions[];
  }

  public getValidationErrors(): IMap<LocalizableError> {
    const errors: IMap<LocalizableError> = {};

    const aggregatedErrorTypes = Object.keys(this.errors).reduce(
      (total, currentErrorKey) => {
        Object.keys(this.errors[currentErrorKey])
          .filter((errorTypeKey) => !!this.errors[currentErrorKey][errorTypeKey])
          .forEach((errorTypeKey) => {
            if (!total[errorTypeKey]) {
              total[errorTypeKey] = [this.errors[currentErrorKey][errorTypeKey].message];
            } else {
              total[errorTypeKey].push(this.errors[currentErrorKey][errorTypeKey].message);
            }
          });
        return total;
      },
      {} as IMap<string[]>,
    );

    for (const key of Object.keys(ParseErrorTypes)) {
      const error = this.composeErrorMessages(aggregatedErrorTypes, ParseErrorTypes[key]);
      if (!!error) {
        errors[ParseErrorTypes[key]] = error;
      }
    }

    if (this.inputError) {
      errors['inputError'] = new LocalizableError(this.inputError.message, PluralForm.One);
    }

    if (this.incompatibleFiltersError) {
      errors['incompatibleFiltersError'] = new LocalizableError(this.incompatibleFiltersError.message, PluralForm.One);
    }

    return errors;
  }

  private composeErrorMessages(aggregatedErrorTypes: IMap<string[]>, errorType: ParseErrorTypes): LocalizableError {
    const errors = aggregatedErrorTypes[errorType];
    if (!errors || errors.length === 0) {
      return null;
    }

    const messages = defaultParseErrorMessages[errorType];

    // return { values: messages.concat ? errors : null, template: errors.length > 1 ? messages.multi : messages.single };
    return new LocalizableError(
      messages.translationKey,
      errors.length > 1 ? PluralForm.Other : PluralForm.One,
      messages.concat ? errors.join(', ') : null,
    );
  }
}
